/**
 * Customize sidebar — per-user left-panel layout.
 *
 * Edits a local draft; nothing changes in the sidebar until Save. Only rows the
 * user can actually see are listed, so the dialog never offers a row that would
 * not render (role / feature-switch gated rows are filtered by the caller).
 * Removing a row only hides it: the page stays reachable by URL and search.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Lock } from "lucide-react";

import {
  DEFAULT_SIDEBAR_LAYOUT,
  SIDEBAR_ITEM_META,
  addItem,
  hiddenItems,
  isInline,
  layoutsEqual,
  moveItem,
  removeItem,
  setDensity,
  type SidebarDensity,
  type SidebarItemId,
  type SidebarLayout,
} from "./sidebarLayout";
import { saveSidebarLayout } from "./useSidebarLayout";

import { Button } from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/overlay/Modal";
import { USER_PREFS_QUERY_KEY } from "@/features/preferences/themePreferences";
import { cn } from "@/lib/utils";

export type CustomizeSidebarDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The layout currently saved (or the default). */
  layout: SidebarLayout;
  /** Whether the signed-in user can see a row at all (role / feature gated). */
  canUse: (id: SidebarItemId) => boolean;
};

const groupTitle = "mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500";
const rowShell = "flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2";

export function CustomizeSidebarDialog({
  isOpen,
  onClose,
  layout,
  canUse,
}: CustomizeSidebarDialogProps): ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SidebarLayout>(layout);

  // Start from the saved layout each time the dialog OPENS — not whenever the
  // `layout` prop changes identity. A background refetch of preferences hands
  // back an equal copy, and resetting then would discard unsaved edits.
  const latestLayout = useRef(layout);
  latestLayout.current = layout;
  useEffect(() => {
    if (isOpen) setDraft(latestLayout.current);
  }, [isOpen]);

  const save = useMutation({
    mutationFn: (next: SidebarLayout) =>
      // The default is stored as "nothing saved" so future default changes reach
      // users who never customised.
      saveSidebarLayout(layoutsEqual(next, DEFAULT_SIDEBAR_LAYOUT) ? null : next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: USER_PREFS_QUERY_KEY });
      onClose();
    },
  });

  const shown = draft.order.filter(canUse);
  const positioned = shown.filter((id) => !isInline(id));
  const hidden = hiddenItems(draft).filter(canUse);
  const unchanged = layoutsEqual(draft, layout);

  const change = (next: SidebarLayout): void => {
    save.reset();
    setDraft(next);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Customize sidebar"
      size="md"
      contentClassName="p-0"
    >
      <div data-testid="customize-sidebar-dialog">
        <p className="px-6 pb-3 text-sm text-slate-500">Changes apply to you only.</p>

        <div className="max-h-[60vh] space-y-5 overflow-y-auto px-6 pb-4">
          <section aria-labelledby="cs-in-sidebar">
            <h3 id="cs-in-sidebar" className={groupTitle}>
              In your sidebar
            </h3>
            <ul className="space-y-2" data-testid="customize-sidebar-shown">
              <li className={rowShell} data-testid="customize-row-home">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-slate-900">Home</span>
                  <span className="block text-xs text-slate-500">Your landing page.</span>
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500">
                  <Lock className="h-3 w-3" aria-hidden />
                  Required
                </span>
              </li>
              {shown.map((id) => {
                const meta = SIDEBAR_ITEM_META[id];
                const index = positioned.indexOf(id);
                return (
                  <li key={id} className={rowShell} data-testid={`customize-row-${id}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-slate-900">
                        {meta.label}
                      </span>
                      <span className="block text-xs text-slate-500">
                        {meta.description}
                      </span>
                    </span>
                    {meta.locked ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500">
                        <Lock className="h-3 w-3" aria-hidden />
                        Required
                      </span>
                    ) : null}
                    {!isInline(id) ? (
                      <span className="flex">
                        <button
                          type="button"
                          aria-label={`Move ${meta.label} up`} title={`Move ${meta.label} up`}
                          data-testid={`customize-up-${id}`}
                          disabled={index <= 0}
                          onClick={() => change(moveItem(draft, id, -1, canUse))}
                          className="rounded-l-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:cursor-default disabled:opacity-40"
                        >
                          <ChevronUp className="h-4 w-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${meta.label} down`} title={`Move ${meta.label} down`}
                          data-testid={`customize-down-${id}`}
                          disabled={index === positioned.length - 1}
                          onClick={() => change(moveItem(draft, id, 1, canUse))}
                          className="-ml-px rounded-r-md border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:cursor-default disabled:opacity-40"
                        >
                          <ChevronDown className="h-4 w-4" aria-hidden />
                        </button>
                      </span>
                    ) : null}
                    {!meta.locked ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={`Hide ${meta.label}`}
                        data-testid={`customize-hide-${id}`}
                        onClick={() => change(removeItem(draft, id))}
                      >
                        Hide
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>

          {hidden.length > 0 ? (
            <section aria-labelledby="cs-hidden">
              <h3 id="cs-hidden" className={groupTitle}>
                Hidden
              </h3>
              <ul className="space-y-2" data-testid="customize-sidebar-hidden">
                {hidden.map((id) => {
                  const meta = SIDEBAR_ITEM_META[id];
                  return (
                    <li key={id} className={rowShell} data-testid={`customize-hidden-${id}`}>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-slate-900">
                          {meta.label}
                        </span>
                        <span className="block text-xs text-slate-500">
                          {meta.description}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={`Show ${meta.label}`}
                        data-testid={`customize-show-${id}`}
                        onClick={() => change(addItem(draft, id))}
                      >
                        Show
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ) : null}

          <section aria-labelledby="cs-display">
            <h3 id="cs-display" className={groupTitle}>
              Display
            </h3>
            <div className={rowShell}>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-slate-900">Row density</span>
                <span className="block text-xs text-slate-500">
                  Compact fits more rows on screen.
                </span>
              </span>
              <span className="inline-flex overflow-hidden rounded-md border border-slate-200">
                {(["comfortable", "compact"] as SidebarDensity[]).map((d) => (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={draft.density === d}
                    data-testid={`customize-density-${d}`}
                    onClick={() => change(setDensity(draft, d))}
                    className={cn(
                      "px-3 py-1 text-xs font-semibold capitalize",
                      draft.density === d
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:bg-slate-50",
                    )}
                  >
                    {d}
                  </button>
                ))}
              </span>
            </div>
          </section>
        </div>

        {save.isError ? (
          <p
            role="alert"
            data-testid="customize-sidebar-error"
            className="px-6 pb-2 text-sm text-red-600"
          >
            Couldn’t save your sidebar. Try again.
          </p>
        ) : null}

        <div className="flex items-center gap-2 border-t border-slate-200 px-6 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="customize-reset"
            disabled={layoutsEqual(draft, DEFAULT_SIDEBAR_LAYOUT)}
            onClick={() => change(DEFAULT_SIDEBAR_LAYOUT)}
          >
            Reset to default
          </Button>
          <span className="flex-1" />
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            data-testid="customize-save"
            disabled={unchanged || save.isPending}
            onClick={() => save.mutate(draft)}
          >
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
